import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { ChevronDown, Download, MoreVertical, Calendar as CalendarIcon, Filter, Clock, CheckCircle2, XCircle, FileText, ArrowRight, ArrowLeft, AlertCircle, Upload, Eye, File, Phone, Mail, MapPin, Building, CreditCard, PenTool, User, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import ValidateSalaryModal from '@/components/employees/ValidateSalaryModal';
import AddEmployeeWizard from '@/components/employees/AddEmployeeWizard';
import PresenceModal from '@/components/employees/PresenceModal';
import SalaryAdjustmentModal from '@/components/employees/SalaryAdjustmentModal';

import CongesAbsencesTab from '@/components/employees/CongesAbsencesTab';
import useGranularPermissions from '@/hooks/useGranularPermissions';


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : BACKEND_URL.endsWith('/') ? `${BACKEND_URL}api` : `${BACKEND_URL}/api`;

const EmployeeDetails = () => {
  const { user } = useGranularPermissions();
  const isAdmin = ['super_admin', 'admin', 'owner', 'gerant'].includes(user?.role);
  
  const [activeTab, setActiveTab] = useState('Présence & Salaire');
  const [showValidateModal, setShowValidateModal] = useState(false);
  const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);
  const [adjustmentType, setAdjustmentType] = useState('prime');
  
  const handleAdjustmentSubmit = async (adjustmentData) => {
    try {
      console.log('API Call (Mocked): POST /adjustments', adjustmentData);
      toast.success('Ajustement enregistré avec succès');
      setShowAdjustmentModal(false);
    } catch (err) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };
  const [showPresenceModal, setShowPresenceModal] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  const handlePresenceSubmit = async (presenceData) => {
    try {
      console.log('API Call (Mocked): POST /attendance', presenceData);
      if (presenceData.id) {
        setAttendanceLogs(prev => prev.map(log => log.id === presenceData.id ? { ...log, status: presenceData.status } : log));
      } else {
        setAttendanceLogs(prev => [{ ...presenceData, id: Date.now(), date: presenceData.date, caisse: '—', montant: '—', validation: 'En attente', salaire: '0,00 DA' }, ...prev]);
      }
      toast.success('Présence enregistrée avec succès');
      setShowPresenceModal(false);
      setSelectedLog(null);
    } catch (err) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const [documents, setDocuments] = useState([]);

  const fileInputRef = React.useRef(null);
  
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const newDoc = {
        id: Date.now(),
        name: file.name,
        type: file.name.split('.').pop().toUpperCase(),
        size: (file.size > 1024 * 1024) 
              ? (file.size / 1024 / 1024).toFixed(1) + ' MB' 
              : (file.size / 1024).toFixed(0) + ' KB',
        date: new Date().toLocaleDateString('fr-FR')
      };
      setDocuments([newDoc, ...documents]);
      toast.success('Document ajouté avec succès');
    }
  };

  const handleDeleteDoc = (id) => {
    setDocuments(documents.filter(d => d.id !== id));
    toast.success('Document supprimé');
  };

  const [notes, setNotes] = useState([]);
  const [currentNote, setCurrentNote] = useState('');

  const handleAddNote = () => {
    if (!currentNote.trim()) return;
    const now = new Date();
    const formattedDate = `le ${now.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })} à ${now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
    
    const newNote = {
      id: Date.now(),
      author: user?.firstName ? `${user.firstName} ${user.lastName || ''}` : 'Admin',
      initials: user?.firstName ? user.firstName[0].toUpperCase() : 'A',
      date: formattedDate,
      content: currentNote.trim()
    };
    
    setNotes([newNote, ...notes]);
    setCurrentNote('');
    toast.success('Note ajoutée avec succès');
  };

  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteContent, setEditingNoteContent] = useState('');

  const handleEditNoteStart = (note) => {
    setEditingNoteId(note.id);
    setEditingNoteContent(note.content);
  };
  
  const handleSaveEditNote = (id) => {
    setNotes(notes.map(note => note.id === id ? { ...note, content: editingNoteContent.trim() } : note));
    setEditingNoteId(null);
    setEditingNoteContent('');
    toast.success('Note modifiée avec succès');
  };

  const handleDeleteNote = (id) => {
    setNotes(notes.filter(note => note.id !== id));
    toast.success('Note supprimée avec succès');
  };

  const [showEditWizard, setShowEditWizard] = useState(false);
  const handleEditSubmit = async (formData) => {
    try {
      console.log('API Call (Mocked): PUT /employees', formData);
      setEmployee(prev => ({
        ...prev,
        ...formData
      }));
      toast.success('Employé modifié avec succès');
      setShowEditWizard(false);
    } catch (err) {
      toast.error('Erreur lors de la modification');
    }
  };


  // Mock employee data as state
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEmployeeDetails = async () => {
      const empId = localStorage.getItem('selectedEmployeeId');
      if (!empId) {
        setLoading(false);
        return;
      }
      try {
        const currentUser = JSON.parse(sessionStorage.getItem('pharmacy_user') || localStorage.getItem('pharmacy_user') || '{}');
        const config = { headers: { 'X-User-Id': currentUser.id } };
        const res = await axios.get(`${API}/users/${empId}`, config);
        const data = res.data;
        
        // Map backend data to the format expected by the UI
        setEmployee({
          id: data.id,
          name: data.first_name || data.username,
          role: data.role || 'caissier',
          status: data.is_active ? 'Active' : 'Inactif',
          matricule: data.matricule || `EMP-${data.id.substring(0, 4)}`,
          hireDate: data.hire_date ? new Date(data.hire_date).toLocaleDateString('fr-FR') : '01/04/2024',
          email: data.email || 'N/A',
          phone: data.phone || 'N/A',
          address: data.address || 'N/A',
          nss: data.nss || 'N/A',
          department: data.department || 'Caisse',
          contractType: data.contract_type || 'CDI - Contrat à durée indéterminée',
          workTime: data.work_time || 'Temps plein',
          startDate: data.hire_date ? data.hire_date.substring(0, 10) : '2024-04-01',
          paymentFrequency: data.payment_frequency || 'Mensuelle',
          baseSalary: data.base_salary || '45000',
          bonuses: data.bonuses || [],
          paymentMethod: data.payment_method || 'transfer',
          bank: data.bank_name || 'N/A',
          accountNumber: data.bank_account || 'N/A',
          accountHolder: data.bank_account_name || (data.first_name || data.username),
          agency: data.bank_agency || 'N/A',
          rib: data.rib || 'N/A',
          bic: data.bic || 'N/A'
        });
      } catch (err) {
        toast.error('Erreur lors du chargement des détails de l\'employé');
      } finally {
        setLoading(false);
      }
    };
    fetchEmployeeDetails();
  }, []);


  const [attendanceLogs, setAttendanceLogs] = useState([]);

  const getStatusStyle = (status) => {
    switch (status) {
      case 'En attente': return 'bg-orange-50 text-orange-600 border-orange-200';
      case 'Validé': return 'bg-emerald-50 text-emerald-600 border-emerald-200';
      case 'Absent': return 'bg-red-50 text-red-600 border-red-200';
      case 'Congé payé': return 'bg-blue-50 text-blue-600 border-blue-200';
      default: return 'bg-gray-50 text-gray-600 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'En attente': return <Clock size={14} className="mr-1.5" />;
      case 'Validé': return <CheckCircle2 size={14} className="mr-1.5" />;
      case 'Absent': return <XCircle size={14} className="mr-1.5" />;
      case 'Congé payé': return <FileText size={14} className="mr-1.5" />;
      default: return null;
    }
  };

  if (loading) return <div className="p-8 text-center">Chargement...</div>;
  if (!employee) return <div className="p-8 text-center text-red-500">Employé introuvable</div>;
  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-10">
      {/* Breadcrumb */}
      <div className="flex items-center text-sm text-gray-500 gap-2 mb-4">
        <button onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'employes' }))} className="hover:text-gray-900 transition-colors">Employés</button>
        <span>›</span>
        <span className="text-gray-900 font-medium">Fiche employé</span>
        <span>›</span>
        <span className="text-gray-900 font-medium">Présence & Salaire</span>
      </div>

      {/* Header Profile */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="p-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-5">
            <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-gray-50 shadow-sm flex-shrink-0">
              <img src={employee.avatar} alt="Avatar" className="w-full h-full object-cover" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 tracking-tight">{employee.firstName} {employee.lastName}</h1>
              <div className="flex items-center gap-3 mt-1.5">
                <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">{employee.status}</span>
                <span className="text-sm font-medium text-gray-600 flex items-center gap-1.5">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                  {employee.role}
                </span>
              </div>
              <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                <span>Matricule : <strong className="text-gray-900">{employee.id}</strong></span>
                <span className="w-1 h-1 rounded-full bg-gray-300"></span>
                <span>Embauché le : <strong className="text-gray-900">{employee.hireDate}</strong></span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <div className="relative">
              <select className="appearance-none bg-white border border-gray-200 rounded-lg pl-10 pr-8 py-2 text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-violet-500">
                <option>Août 2026</option>
                <option>Juillet 2026</option>
              </select>
              <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
            </div>
            
            {isAdmin && (
              <>
                <Button variant="outline" className="text-gray-700 border-gray-200 bg-white shadow-sm">
                  <Download className="mr-2 h-4 w-4" />
                  Exporter
                </Button>
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button className="bg-violet-600 hover:bg-violet-700 text-white shadow-sm">
                      Actions <MoreVertical className="ml-2 h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 p-2 rounded-xl shadow-xl border-gray-100">
                    <DropdownMenuItem onClick={() => setShowEditWizard(true)} className="text-sm font-medium p-2 cursor-pointer rounded-lg hover:bg-gray-50 text-violet-700">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2 text-violet-600"><path d="M12 20h9"></path><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path></svg>
                      Modifier la fiche employé
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setShowPresenceModal(true)} className="text-sm font-medium p-2 cursor-pointer rounded-lg hover:bg-gray-50 text-sky-700">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2 text-sky-600"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                      Gérer la présence
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="my-1 bg-gray-100" />
                    <DropdownMenuItem onClick={() => { setAdjustmentType('prime'); setShowAdjustmentModal(true); }} className="text-sm font-medium p-2 cursor-pointer rounded-lg hover:bg-gray-50">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2 text-gray-500"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="16"></line><line x1="8" y1="12" x2="16" y2="12"></line></svg>
                      Ajouter une prime / indemnité
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => { setAdjustmentType('avance'); setShowAdjustmentModal(true); }} className="text-sm font-medium p-2 cursor-pointer rounded-lg hover:bg-gray-50">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2 text-gray-500"><circle cx="12" cy="12" r="10"></circle><line x1="8" y1="12" x2="16" y2="12"></line></svg>
                      Ajouter une avance sur salaire
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => { setAdjustmentType('retenue'); setShowAdjustmentModal(true); }} className="text-sm font-medium p-2 cursor-pointer rounded-lg hover:bg-gray-50">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2 text-gray-500"><path d="M12 20h9"></path><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path></svg>
                      Ajouter une retenue
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => { setAdjustmentType('ajustement'); setShowAdjustmentModal(true); }} className="text-sm font-medium p-2 cursor-pointer rounded-lg hover:bg-gray-50">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2 text-gray-500"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                      Ajuster le salaire (exceptionnel)
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="my-1 bg-gray-100" />
                    <DropdownMenuItem onClick={() => setShowValidateModal(true)} className="text-sm font-medium p-2 cursor-pointer rounded-lg hover:bg-emerald-50 text-emerald-700">
                      <CheckCircle2 size={16} className="mr-2" />
                      Valider le salaire du mois
                    </DropdownMenuItem>
                    <DropdownMenuItem className="text-sm font-medium p-2 cursor-pointer rounded-lg hover:bg-gray-50">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2 text-gray-500"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
                      Enregistrer le paiement
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="my-1 bg-gray-100" />
                    <DropdownMenuItem className="text-sm font-medium p-2 cursor-pointer rounded-lg hover:bg-gray-50">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2 text-gray-500"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
                      Documents
                    </DropdownMenuItem>
                    <DropdownMenuItem className="text-sm font-medium p-2 cursor-pointer rounded-lg hover:bg-gray-50">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2 text-gray-500"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                      Historique des salaires
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="my-1 bg-gray-100" />
                    <DropdownMenuItem className="text-sm font-medium p-2 cursor-pointer rounded-lg hover:bg-red-50 text-red-600">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><line x1="9" y1="9" x2="15" y2="15"></line><line x1="15" y1="9" x2="9" y2="15"></line></svg>
                      Suspendre / Terminer l'emploi
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex px-6 border-t border-gray-100 overflow-x-auto no-scrollbar">
          {['Informations', 'Contrat', 'Présence & Salaire', 'Congés & Absences', 'Documents', ...(isAdmin ? ['Notes'] : [])].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-4 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors ${
                activeTab === tab 
                  ? 'border-violet-600 text-violet-700' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'Présence & Salaire' && (
        <div className="space-y-6">
          {/* Top Cards */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <Card className="border-violet-100 bg-violet-50/50 shadow-sm">
              <CardContent className="p-4 flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center text-violet-600 shrink-0">
                  <CalendarIcon size={20} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-violet-900">Jours prévus</p>
                  <p className="text-2xl font-bold text-violet-700 my-1">0</p>
                  <p className="text-xs text-violet-600/70 font-medium">Ce mois</p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-emerald-100 bg-emerald-50/50 shadow-sm">
              <CardContent className="p-4 flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center text-emerald-600 shrink-0">
                  <CheckCircle2 size={20} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-emerald-900">Jours travaillés validés</p>
                  <p className="text-2xl font-bold text-emerald-700 my-1">0</p>
                  <p className="text-xs text-emerald-600/70 font-medium">0%</p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-orange-100 bg-orange-50/50 shadow-sm">
              <CardContent className="p-4 flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center text-orange-600 shrink-0">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 15v4c0 1.1.9 2 2 2h14a2 2 0 0 0 2-2v-4M17 9l-5-5-5 5M12 12v-8"/></svg>
                </div>
                <div>
                  <p className="text-sm font-semibold text-orange-900">Congés payés</p>
                  <p className="text-2xl font-bold text-orange-700 my-1">0</p>
                  <p className="text-xs text-orange-600/70 font-medium">0%</p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-red-100 bg-red-50/50 shadow-sm">
              <CardContent className="p-4 flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center text-red-600 shrink-0">
                  <AlertCircle size={20} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-red-900">Absences</p>
                  <p className="text-2xl font-bold text-red-700 my-1">0</p>
                  <p className="text-xs text-red-600/70 font-medium">0%</p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-blue-100 bg-blue-50/50 shadow-sm">
              <CardContent className="p-4 flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center text-blue-600 shrink-0">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"></rect><line x1="2" y1="10" x2="22" y2="10"></line></svg>
                </div>
                <div>
                  <p className="text-sm font-semibold text-blue-900">Salaire estimé</p>
                  <p className="text-xl font-bold text-blue-700 my-1 tracking-tight">0,00 DA</p>
                  <p className="text-xs text-blue-600/70 font-medium">Avant primes et retenues</p>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="flex flex-col lg:flex-row gap-6">
            {/* Left Column: Details table */}
            <div className="flex-1 space-y-4">
              <div className="flex justify-between items-end mb-4">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Détails des journées – Août 2026</h2>
                  <div className="flex items-center gap-2 mt-3">
                    <span className="px-3 py-1 bg-violet-100 text-violet-700 rounded-full text-xs font-semibold cursor-pointer border border-violet-200">Tout (0)</span>
                    <span className="px-3 py-1 bg-white text-gray-600 rounded-full text-xs font-medium cursor-pointer border border-gray-200 hover:bg-gray-50 transition-colors">En attente (0)</span>
                    <span className="px-3 py-1 bg-white text-gray-600 rounded-full text-xs font-medium cursor-pointer border border-gray-200 hover:bg-gray-50 transition-colors">Validées (0)</span>
                    <span className="px-3 py-1 bg-white text-gray-600 rounded-full text-xs font-medium cursor-pointer border border-gray-200 hover:bg-gray-50 transition-colors">Autres (0)</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" className="text-gray-700 border-gray-200 bg-white h-9 shadow-sm">
                    <CalendarIcon className="mr-2 h-4 w-4" /> Calendrier
                  </Button>
                  <Button variant="outline" className="text-gray-700 border-gray-200 bg-white h-9 shadow-sm">
                    <Filter className="mr-2 h-4 w-4" /> Filtres
                  </Button>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-gray-50 text-gray-500 font-semibold border-b border-gray-100 text-xs">
                      <tr>
                        <th className="px-6 py-4 rounded-tl-xl">DATE</th>
                        <th className="px-6 py-4">STATUT</th>
                        <th className="px-6 py-4">CAISSE</th>
                        <th className="px-6 py-4">MONTANT CLÔTURÉ</th>
                        <th className="px-6 py-4">VALIDATION</th>
                        <th className="px-6 py-4 text-right">SALAIRE JOURNALIER</th>
                        <th className="px-6 py-4 rounded-tr-xl"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {attendanceLogs.map((log) => (
                        <tr key={log.id} className="hover:bg-gray-50/50 transition-colors">
                          <td className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap">{log.date}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${getStatusStyle(log.status)}`}>
                              {getStatusIcon(log.status)}
                              {log.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {log.caisse === 'Clôturée' ? (
                              <div>
                                <span className="font-medium text-gray-900">{log.caisse}</span>
                                <span className="text-gray-500 text-xs ml-2">{log.caisseRef}</span>
                              </div>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap">{log.montant}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {log.validation === 'Validé' ? (
                              <div>
                                <span className="text-emerald-600 font-medium">Validé</span>
                                {log.validationDate && <p className="text-gray-500 text-xs">{log.validationDate}</p>}
                              </div>
                            ) : (
                              <span className="text-gray-600 font-medium">{log.validation}</span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-right font-medium text-gray-900 whitespace-nowrap">{log.salaire}</td>
                          <td className="px-4 py-4 text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button className="text-gray-400 hover:text-gray-700 p-2 rounded-lg hover:bg-gray-100 transition-colors"><MoreVertical size={16} /></button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-48 p-2 rounded-xl shadow-xl border-gray-100">
                                <DropdownMenuItem onClick={() => { setSelectedLog(log); setShowPresenceModal(true); }} className="text-sm font-medium p-2 cursor-pointer rounded-lg hover:bg-gray-50 text-gray-700">
                                  <PenTool className="mr-2 h-4 w-4 text-gray-500" /> Modifier
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => setAttendanceLogs(prev => prev.filter(l => l.id !== log.id))} className="text-sm font-medium p-2 cursor-pointer rounded-lg hover:bg-red-50 text-red-600">
                                  <XCircle className="mr-2 h-4 w-4" /> Supprimer
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2 text-gray-500 font-medium">
                    Afficher
                    <select className="border border-gray-200 rounded p-1 bg-white focus:outline-none focus:ring-1 focus:ring-violet-500">
                      <option>10</option>
                      <option>20</option>
                    </select>
                  </div>
                  <div className="text-gray-500 font-medium">0 sur 0</div>
                  <div className="flex gap-1">
                    <button className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:bg-gray-50 transition-colors"><ArrowLeft size={14} /></button>
                    <button className="w-8 h-8 flex items-center justify-center rounded-lg bg-violet-600 text-white font-medium shadow-sm transition-colors">1</button>
                    <button className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors"><ArrowRight size={14} /></button>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-2 text-sm text-gray-500 mt-2 px-2">
                <AlertCircle size={14} className="text-blue-500" />
                <span>Une journée est considérée comme travaillée après validation de la caisse par l'administrateur.</span>
              </div>

              {/* Workflow visual */}
              <div className="mt-8 bg-white border border-gray-200 rounded-xl shadow-sm p-6">
                <h3 className="font-bold text-gray-900 mb-6">Workflow de la journée travaillée</h3>
                <div className="flex items-center justify-between gap-2 overflow-x-auto pb-2 min-w-max text-sm">
                  
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-violet-100 text-violet-600 flex items-center justify-center shrink-0">
                      <CalendarIcon size={20} />
                    </div>
                    <div>
                      <p className="font-bold text-gray-900 leading-none">Employé travaille</p>
                      <p className="text-gray-500 text-xs mt-1">Effectue sa caisse</p>
                    </div>
                  </div>
                  
                  <ArrowRight className="text-gray-300" size={16} />
                  
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center shrink-0">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
                    </div>
                    <div>
                      <p className="font-bold text-gray-900 leading-none">Caisse soumise</p>
                      <p className="text-gray-500 text-xs mt-1">Statut : En attente</p>
                    </div>
                  </div>

                  <ArrowRight className="text-gray-300" size={16} />
                  
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0">
                      <CheckCircle2 size={20} />
                    </div>
                    <div>
                      <p className="font-bold text-gray-900 leading-none">Admin valide la caisse</p>
                      <p className="text-gray-500 text-xs mt-1">Jour marqué travaillé</p>
                    </div>
                  </div>

                  <ArrowRight className="text-gray-300" size={16} />
                  
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-violet-100 text-violet-600 flex items-center justify-center shrink-0">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"></line><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path></svg>
                    </div>
                    <div>
                      <p className="font-bold text-gray-900 leading-none">Intégrée au salaire</p>
                      <p className="text-gray-500 text-xs mt-1">Calcul automatique</p>
                    </div>
                  </div>

                  <ArrowRight className="text-gray-300" size={16} />

                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"></rect><line x1="2" y1="10" x2="22" y2="10"></line></svg>
                    </div>
                    <div>
                      <p className="font-bold text-gray-900 leading-none">Paiement du salaire</p>
                      <p className="text-gray-500 text-xs mt-1">Sortie Cash / Banque</p>
                    </div>
                  </div>
                  
                </div>
              </div>

            </div>

            {/* Right Column: Summaries & Actions */}
            <div className="w-full lg:w-[350px] space-y-6">
              
              {/* Résumé du salaire */}
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Résumé du salaire – Août 2026</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between py-1">
                    <span className="text-gray-600">Salaire mensuel (contrat)</span>
                    <span className="font-bold text-gray-900">45 000,00 DA</span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-gray-100 pb-3">
                    <span className="text-gray-500">Base de calcul</span>
                    <span className="font-medium text-gray-600">26 jours / mois</span>
                  </div>
                  
                  <div className="flex justify-between py-1 pt-2">
                    <span className="text-gray-600">Salaire jours travaillés (18 j)</span>
                    <span className="font-medium text-gray-900">31 153,86 DA</span>
                  </div>
                  <div className="flex justify-between py-1">
                    <span className="text-gray-600">Congés payés (1 j)</span>
                    <span className="font-medium text-emerald-600">+ 1 730,77 DA</span>
                  </div>
                  <div className="flex justify-between py-1">
                    <span className="text-gray-600">Prime de rendement</span>
                    <span className="font-medium text-emerald-600">+ 5 000,00 DA</span>
                  </div>
                  <div className="flex justify-between py-1">
                    <span className="text-gray-600">Avance sur salaire</span>
                    <span className="font-medium text-red-600">- 10 000,00 DA</span>
                  </div>
                </div>

                <div className="mt-6 bg-violet-50 rounded-xl p-4 text-center border border-violet-100 shadow-sm">
                  <p className="text-violet-800 text-sm font-bold tracking-wide uppercase mb-1">Net à payer <span className="text-violet-600/70 capitalize font-medium text-xs">(Estimation)</span></p>
                  <p className="text-2xl font-bold text-violet-700">27 884,63 DA</p>
                </div>
              </div>

              {/* Règles de calcul */}
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Règles de calcul</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between py-1">
                    <span className="text-gray-600">Mode de rémunération</span>
                    <span className="font-medium text-gray-900">Salaire mensuel</span>
                  </div>
                  <div className="flex justify-between py-1">
                    <span className="text-gray-600">Salaire mensuel</span>
                    <span className="font-medium text-gray-900">45 000,00 DA</span>
                  </div>
                  <div className="flex justify-between py-1">
                    <span className="text-gray-600">Base de jours</span>
                    <span className="font-medium text-gray-900">26 jours / mois</span>
                  </div>
                  <div className="flex justify-between py-1">
                    <span className="text-gray-600">Salaire journalier</span>
                    <span className="font-medium text-gray-900">1 730,77 DA</span>
                  </div>
                </div>
              </div>

              {/* Actions rapides */}
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Actions rapides</h3>
                <div className="space-y-3">
                  <button className="w-full flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:border-violet-300 hover:bg-violet-50/50 transition-colors group">
                    <div className="flex items-center gap-3 text-sm font-medium text-gray-700 group-hover:text-violet-700">
                      <Clock size={18} className="text-gray-400 group-hover:text-violet-500" />
                      Voir l'historique des salaires
                    </div>
                  </button>
                  <button className="w-full flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:border-emerald-300 hover:bg-emerald-50/50 transition-colors group">
                    <div className="flex items-center gap-3 text-sm font-medium text-gray-700 group-hover:text-emerald-700">
                      <div className="w-5 h-5 rounded-full border-2 border-current flex items-center justify-center font-bold text-xs">+</div>
                      Ajouter une prime / indemnité
                    </div>
                  </button>
                  <button className="w-full flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:border-red-300 hover:bg-red-50/50 transition-colors group">
                    <div className="flex items-center gap-3 text-sm font-medium text-gray-700 group-hover:text-red-700">
                      <div className="w-5 h-5 rounded-full border-2 border-current flex items-center justify-center font-bold text-xs">-</div>
                      Ajouter une avance
                    </div>
                  </button>
                  
                  <Button 
                    onClick={() => setShowValidateModal(true)} 
                    className="w-full mt-2 bg-violet-600 hover:bg-violet-700 text-white font-semibold h-11"
                  >
                    <CheckCircle2 className="mr-2 h-5 w-5" />
                    Valider le salaire du mois
                  </Button>
                </div>
              </div>

            </div>
          </div>
        </div>
      )}

      {/* Informations Tab */}
      {activeTab === 'Informations' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="col-span-1 md:col-span-2 flex justify-end mb-2">
            {isAdmin && (
              <Button onClick={() => setShowEditWizard(true)} className="bg-violet-600 hover:bg-violet-700 text-white shadow-sm">
                <PenTool className="mr-2 h-4 w-4" />
                Modifier les informations
              </Button>
            )}
          </div>
          <Card className="border-gray-200 shadow-sm">
            <CardContent className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <User size={20} className="text-violet-600" /> Informations personnelles
                </h3>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Nom</p>
                    <p className="font-medium text-gray-900">{employee.lastName || '—'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Prénom</p>
                    <p className="font-medium text-gray-900">{employee.firstName || '—'}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Date de naissance</p>
                    <p className="font-medium text-gray-900">{employee.birthDate ? new Date(employee.birthDate).toLocaleDateString('fr-FR') : '—'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Lieu de naissance</p>
                    <p className="font-medium text-gray-900">{employee.birthPlace || '—'}</p>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Nationalité</p>
                  <p className="font-medium text-gray-900">{employee.nationality || '—'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Carte d'identité nationale</p>
                  <p className="font-medium text-gray-900">{employee.nationalId || '—'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-gray-200 shadow-sm">
            <CardContent className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <Building size={20} className="text-sky-600" /> Informations sociales
                </h3>
              </div>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-500">N° Sécurité sociale (CNAS)</p>
                  <p className="font-medium text-gray-900">{employee.socialSecurityNum || '—'}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Organisme</p>
                    <p className="font-medium text-gray-900">{employee.socialSecurityOrg || '—'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Date d'affiliation</p>
                    <p className="font-medium text-gray-900">{employee.affiliationDate ? new Date(employee.affiliationDate).toLocaleDateString('fr-FR') : '—'}</p>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Mutuelle</p>
                  <p className="font-medium text-gray-900">{employee.mutuelle || '—'}</p>
                </div>
              </div>

              <h3 className="text-lg font-bold text-gray-900 mt-8 mb-4 flex items-center gap-2">
                <AlertCircle size={20} className="text-orange-600" /> Contact d'urgence
              </h3>
              <div className="space-y-4 bg-orange-50/50 p-4 rounded-xl border border-orange-100">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-orange-600/80">Nom</p>
                    <p className="font-medium text-orange-900">{employee.emergencyContactName || '—'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-orange-600/80">Lien</p>
                    <p className="font-medium text-orange-900">{employee.emergencyContactRelation || '—'}</p>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-orange-600/80">Téléphone</p>
                  <p className="font-medium text-orange-900">{employee.emergencyContactPhone || '—'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-gray-200 shadow-sm md:col-span-2">
            <CardContent className="p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <MapPin size={20} className="text-emerald-600" /> Coordonnées
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-emerald-50 text-emerald-600 rounded-lg"><Phone size={18} /></div>
                  <div>
                    <p className="text-sm text-gray-500">Téléphone</p>
                    <p className="font-medium text-gray-900">{employee.phone || '—'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-emerald-50 text-emerald-600 rounded-lg"><Mail size={18} /></div>
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-medium text-gray-900">{employee.email || '—'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-emerald-50 text-emerald-600 rounded-lg"><MapPin size={18} /></div>
                  <div>
                    <p className="text-sm text-gray-500">Adresse</p>
                    <p className="font-medium text-gray-900">{employee.address || '—'}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Contrat Tab */}
      {activeTab === 'Contrat' && (
        <div className="space-y-6">
          <div className="flex justify-end mb-2">
            {isAdmin && (
              <Button onClick={() => setShowEditWizard(true)} className="bg-violet-600 hover:bg-violet-700 text-white shadow-sm">
                <PenTool className="mr-2 h-4 w-4" />
                Modifier le contrat
              </Button>
            )}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <Card className="border-gray-200 shadow-sm">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <FileText size={20} className="text-orange-600" /> Détails de l'emploi
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                    <div>
                      <p className="text-sm text-gray-500">Fonction</p>
                      <p className="font-medium text-gray-900">{employee.role || '—'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Département</p>
                      <p className="font-medium text-gray-900">{employee.department || '—'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Type de contrat</p>
                      <p className="font-medium text-gray-900">{employee.contractType || '—'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Temps de travail</p>
                      <p className="font-medium text-gray-900">{employee.workTime || '—'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Date de début</p>
                      <p className="font-medium text-gray-900">{employee.startDate ? new Date(employee.startDate).toLocaleDateString('fr-FR') : (employee.hireDate || '—')}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Périodicité</p>
                      <p className="font-medium text-gray-900">{employee.paymentFrequency || '—'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-gray-200 shadow-sm">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <CheckCircle2 size={20} className="text-emerald-600" /> Rémunération & Primes
                  </h3>
                  <div className="flex justify-between items-center p-4 bg-emerald-50 rounded-xl border border-emerald-100 mb-6">
                    <div>
                      <p className="text-sm text-emerald-800 font-medium">Salaire de base</p>
                      <p className="text-2xl font-bold text-emerald-600">{employee.baseSalary ? `${parseFloat(employee.baseSalary).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} DA` : '—'}</p>
                    </div>
                  </div>
                  
                  <h4 className="font-semibold text-gray-900 mb-3">Primes récurrentes</h4>
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="w-full text-sm text-left">
                      <thead className="bg-gray-50 text-gray-500 font-medium border-b border-gray-100">
                        <tr>
                          <th className="px-4 py-2">Nom</th>
                          <th className="px-4 py-2">Type</th>
                          <th className="px-4 py-2 text-right">Montant</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {employee.bonuses && employee.bonuses.map((bonus, idx) => (
                          <tr key={idx}>
                            <td className="px-4 py-3 font-medium text-gray-900">{bonus.name}</td>
                            <td className="px-4 py-3 text-gray-600">{bonus.type}</td>
                            <td className="px-4 py-3 text-right font-medium text-emerald-600">+{bonus.amount} DA</td>
                          </tr>
                        ))}
                        {(!employee.bonuses || employee.bonuses.length === 0) && (
                          <tr>
                            <td colSpan="3" className="px-4 py-3 text-center text-gray-500">Aucune prime</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="space-y-6">
              <Card className="border-gray-200 shadow-sm">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <CreditCard size={20} className="text-indigo-600" /> Coordonnées bancaires
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-3 bg-indigo-50 text-indigo-700 rounded-lg border border-indigo-100">
                      <CreditCard size={20} />
                      <span className="font-semibold">{employee.paymentMethod === 'transfer' ? 'Virement bancaire' : employee.paymentMethod === 'cash' ? 'Espèces' : 'Chèque'}</span>
                    </div>
                    
                    {employee.paymentMethod === 'transfer' ? (
                      <>
                        <div>
                          <p className="text-sm text-gray-500">Banque</p>
                          <p className="font-medium text-gray-900">{employee.bank || '—'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">N° de compte (RIB)</p>
                          <p className="font-mono text-gray-900 tracking-wider">{employee.accountNumber || '—'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Titulaire</p>
                          <p className="font-medium text-gray-900">{employee.accountHolder || '—'}</p>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-sm text-gray-500">Agence</p>
                            <p className="font-medium text-gray-900">{employee.agency || '—'}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Clé</p>
                            <p className="font-medium text-gray-900">{employee.rib || '—'}</p>
                          </div>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">BIC / SWIFT</p>
                          <p className="font-medium text-gray-900">{employee.bic || '—'}</p>
                        </div>
                      </>
                    ) : (
                      <div className="text-sm text-gray-500 mt-2 italic">
                        {employee.paymentMethod === 'cash' ? 'Paiement en espèces, aucune coordonnée bancaire nécessaire.' : 'Paiement par chèque.'}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      )}

      {/* Documents Tab */}
      {activeTab === 'Documents' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-bold text-gray-900">Documents de l'employé</h3>
            {isAdmin && (
              <div>
                <input type="file" ref={fileInputRef} className="hidden" onChange={handleFileUpload} />
                <Button onClick={() => fileInputRef.current.click()} className="bg-violet-600 hover:bg-violet-700 text-white shadow-sm">
                  <Upload className="mr-2 h-4 w-4" /> Ajouter un document
                </Button>
              </div>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {documents.length === 0 && (
              <div className="col-span-full py-8 text-center text-gray-500 bg-gray-50 rounded-xl border border-gray-100">
                Aucun document n'a été ajouté.
              </div>
            )}
            {/* Document Card */}
            {documents.map((doc) => (
              <Card key={doc.id} className="border-gray-200 shadow-sm hover:shadow-md transition-shadow group flex flex-col">
                <CardContent className="p-5 flex-1 flex flex-col">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-bold text-sm shrink-0 ${
                      doc.type === 'PDF' ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-600'
                    }`}>
                      {doc.type}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 truncate" title={doc.name}>{doc.name}</p>
                      <p className="text-sm text-gray-500 mt-0.5">{doc.size} • Ajouté le {doc.date}</p>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-auto pt-4 border-t border-gray-100">
                    <Button variant="ghost" className="flex-1 text-gray-600 hover:text-violet-600 hover:bg-violet-50 h-9 text-xs">
                      <Eye className="mr-1.5 h-4 w-4" /> Voir
                    </Button>
                    <Button variant="ghost" className="flex-1 text-gray-600 hover:text-violet-600 hover:bg-violet-50 h-9 text-xs">
                      <Download className="mr-1.5 h-4 w-4" /> Télécharger
                    </Button>
                    {isAdmin && (
                      <Button variant="ghost" onClick={() => handleDeleteDoc(doc.id)} className="flex-1 text-red-600 hover:bg-red-50 h-9 text-xs">
                        <Trash2 className="mr-1.5 h-4 w-4" /> Supprimer
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Notes Tab */}
      {(isAdmin && activeTab === 'Notes') && (
        <div className="max-w-3xl space-y-6">
          <Card className="border-gray-200 shadow-sm">
            <CardContent className="p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Ajouter une note</h3>
              <textarea 
                className="w-full p-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none min-h-[100px]"
                placeholder="Rédigez une note, un commentaire ou un rappel concernant cet employé..."
                value={currentNote}
                onChange={(e) => setCurrentNote(e.target.value)}
              ></textarea>
              <div className="flex justify-end mt-4">
                <Button onClick={handleAddNote} disabled={!currentNote.trim()} className="bg-violet-600 hover:bg-violet-700 text-white disabled:opacity-50">
                  Enregistrer la note
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-4">
            <h3 className="text-lg font-bold text-gray-900 mb-4 mt-8">Historique des notes</h3>
            
            {notes.map(note => (
              <Card key={note.id} className="border-gray-200 shadow-sm relative group">
                <CardContent className="p-5 flex gap-4">
                  <div className="w-10 h-10 rounded-full bg-violet-100 text-violet-600 flex items-center justify-center shrink-0">
                    <span className="font-bold">{note.initials}</span>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-bold text-gray-900">{note.author}</span>
                      <span className="text-sm text-gray-500">{note.date}</span>
                    </div>
                    {editingNoteId === note.id ? (
                      <div className="mt-2">
                        <textarea 
                          className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none min-h-[80px] text-sm"
                          value={editingNoteContent}
                          onChange={(e) => setEditingNoteContent(e.target.value)}
                        />
                        <div className="flex justify-end gap-2 mt-2">
                          <Button variant="outline" size="sm" onClick={() => setEditingNoteId(null)} className="h-8 text-xs">Annuler</Button>
                          <Button size="sm" onClick={() => handleSaveEditNote(note.id)} disabled={!editingNoteContent.trim()} className="bg-violet-600 hover:bg-violet-700 text-white h-8 text-xs disabled:opacity-50">Enregistrer</Button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-700 whitespace-pre-wrap">{note.content}</p>
                    )}
                  </div>
                  
                  {isAdmin && editingNoteId !== note.id && (
                    <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="text-gray-400 hover:text-gray-700 p-2 rounded-lg hover:bg-gray-100 transition-colors">
                            <MoreVertical size={16} />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-40 p-2 rounded-xl shadow-xl border-gray-100">
                          <DropdownMenuItem onClick={() => handleEditNoteStart(note)} className="text-sm font-medium p-2 cursor-pointer rounded-lg hover:bg-gray-50 text-gray-700">
                            <PenTool className="mr-2 h-4 w-4 text-gray-500" /> Modifier
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDeleteNote(note.id)} className="text-sm font-medium p-2 cursor-pointer rounded-lg hover:bg-red-50 text-red-600">
                            <Trash2 className="mr-2 h-4 w-4" /> Supprimer
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
            
            {notes.length === 0 && (
              <p className="text-gray-500 italic">Aucune note pour le moment.</p>
            )}
          </div>
        </div>
      )}

      {activeTab === 'Congés & Absences' && (
        <CongesAbsencesTab isAdmin={isAdmin} />
      )}




      {showAdjustmentModal && (
        <SalaryAdjustmentModal 
          open={showAdjustmentModal} 
          onOpenChange={setShowAdjustmentModal}
          employee={employee}
          adjustmentType={adjustmentType}
          onSubmit={handleAdjustmentSubmit}
        />
      )}

      {showPresenceModal && (
        <PresenceModal 
          open={showPresenceModal} 
          onOpenChange={(val) => { setShowPresenceModal(val); if (!val) setSelectedLog(null); }}
          employee={employee}
          onSubmit={handlePresenceSubmit}
          initialData={selectedLog}
        />
      )}

      {showEditWizard && (
        <AddEmployeeWizard 
          open={showEditWizard} 
          onOpenChange={setShowEditWizard} 
          editMode={true}
          initialData={employee}
          onSubmit={handleEditSubmit}
        />
      )}

      {showValidateModal && (
        <ValidateSalaryModal 
          open={showValidateModal} 
          onOpenChange={setShowValidateModal} 
        />
      )}
    </div>
  );
};

export default EmployeeDetails;
